import { AirportHasUser, UserWithMarket } from '@/models/gen/graphql';
import { Datetime, getClasses, minutesToOffset, titleCase } from '@/utils';

import ActiveSwitch from '@/components/ActiveSwitch';
import { Button } from 'react-bootstrap';
import Input from '@/common/Input';
import { ReactElement } from 'react';
import Table from '@/common/Table';

export type UsersTableRowProps = {
  data: UserWithMarket;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDoubleClick: () => void;
  onResetPassword: (user: UserWithMarket) => void;
  onToggleActive: (user: UserWithMarket, isChecked: boolean) => Promise<void>;
};

// Consumed by EditUsersModal/ProfileHeader.tsx for the full User type's airportCodes:
// AirportHasUser[]. ProfileHeader falls back to the Users table row (a UserWithMarket, whose
// airportCodes is the raw comma-joined string - see formatUserWithMarketAirportCodes below)
// while the dedicated full-User fetch is still loading, so this must accept either shape.
export const formatAirportCodes = (airportCodes: AirportHasUser[] | string = []): string => {
  if (typeof airportCodes === 'string') return formatUserWithMarketAirportCodes(airportCodes);
  const codes = Array.from(new Set(airportCodes.map((a) => a.airportCode)));
  if (codes.includes('*')) return 'All Airports';
  return codes.sort().join(', ');
};

// UserWithMarket.airportCodes is the raw, comma-joined view column (e.g. "FLL,MIA" or "*" for
// the wildcard/all-markets permission, "" for no access) - the backend no longer translates "*"
// to "All Airports", so that display rule lives here now.
export const formatUserWithMarketAirportCodes = (airportCodes: string = ''): string => {
  const codes = airportCodes.split(',').filter(Boolean);
  if (codes.includes('*')) return 'All Airports';
  return codes.sort().join(', ');
};

const formatLastLogin = (data: UserWithMarket): string => {
  if (!data?.lastLoginUtc) return 'Never Logged In';
  const lastLoginDatetime = new Datetime(data.lastLoginUtc).applyTimezone(data.lastLoginTimezone);
  const offset = data.lastLoginOffset || minutesToOffset(lastLoginDatetime.utcOffset());
  return lastLoginDatetime.applyOffset(offset).fullFrontendWithTzDatetime;
};

const UsersTableRow = ({
  data,
  isSelected,
  onSelect,
  onDoubleClick,
  onResetPassword,
  onToggleActive,
}: UsersTableRowProps): ReactElement => {
  const airportCodes = formatUserWithMarketAirportCodes(data.airportCodes);

  return (
    <Table.Row id={data?.id} className={getClasses(isSelected ? 'selected' : undefined)} onDoubleClick={onDoubleClick} virtualize={false}>
      <Table.Cell className="SELECT">
        <Input type="checkbox" checked={isSelected} onClick={(): void => onSelect(data.id)} />
      </Table.Cell>
      <Table.Cell className="USERNAME" data-placeholder="--">
        {data.username || ''}
      </Table.Cell>
      <Table.Cell className="NAME" data-placeholder="--">
        {`${data.firstName || ''} ${data.lastName || ''}`.trim()}
      </Table.Cell>
      <Table.Cell className="EMAIL" data-placeholder="--">
        {data.email || ''}
      </Table.Cell>
      <Table.Cell className="ROLE" data-placeholder="--">
        {titleCase(data?.roleName) || ''}
      </Table.Cell>
      <Table.Cell className="LAST-LOGIN" data-placeholder="Never Logged In">
        {formatLastLogin(data)}
      </Table.Cell>
      <Table.Cell className="AIRPORTS" data-placeholder="--">
        {airportCodes && <div className="airport-codes">{airportCodes}</div>}
      </Table.Cell>
      <Table.Cell className="EMPLOYEE-ID" data-placeholder="--">
        {data.employeeId}
      </Table.Cell>
      <Table.Cell className="ACTIVE" data-placeholder="--">
        <ActiveSwitch checked={!!data.active} onChange={(isChecked): Promise<void> => onToggleActive(data, isChecked)} />
      </Table.Cell>
      <Table.Cell className="RESET-PW" data-placeholder="--">
        <Button onClick={(): void => onResetPassword(data)} variant="icon">
          <i className="sv sv-rotation-lock fs-5" />
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

export default UsersTableRow;
